import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { marketingAPI, agencyAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Save, ArrowLeft, Plus, Trash2, Send, GripVertical, ImageIcon, Upload, X, Copy, Search } from 'lucide-react';
import { MarkdownEditor } from '@/components/common/MarkdownEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import { MARKETING_CATEGORIES as CATEGORIES } from '@/constants/marketingCategories';

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
];

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || `field_${Math.floor(Math.random() * 1000)}`;

function PagePreview({ templateId, page }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    marketingAPI.previewBlob(templateId, page).then((res) => {
      const url = URL.createObjectURL(res.data);
      revoke = url;
      setSrc(url);
    }).catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, page]);
  if (!src) return <div className="h-48 bg-slate-100 rounded flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={18} /></div>;
  return <img src={src} alt={`Page ${page}`} className="w-full rounded border bg-slate-50" />;
}

export default function MarketingTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const isNew = !id;

  const [step, setStep] = useState(isNew ? 'upload' : 'edit');
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({
    title: '', description: '', category: 'business_card',
    audience: ['all'], carrier_filter: [],
    pricing_tiers: [],
    auto_notify: true,
    // Phase 27.30.32 — Precise visibility scope
    visibility_scope: 'all',
    visibility_agency_id: null,
    // Phase 27.50 — Ecommerce Storefront
    is_purchasable: false,
    unit_price: '',
    cost: '',
    sku: '',
    stock_qty: '',
    weight_oz: '',
  });
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [loading, setLoading] = useState(false);
  // Phase 27.50.C.36 — Diagnostic panel state (recipient list preview
  // and single-address eligibility check). Kept at the top-level of
  // the component to preserve React Hooks order across early returns.
  const [lastSendPreview, setLastSendPreview] = useState(null);
  const [checkEmail, setCheckEmail] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  // Agency list for the "agency_tree" scope picker. Loaded once when the
  // admin opens the editor.
  const [agencies, setAgencies] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await agencyAPI.list();
        if (!cancelled) setAgencies(r.data || []);
      } catch { /* non-fatal — picker just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let pollTimer;
    const fetchOnce = async () => {
      try {
        const res = await marketingAPI.getTemplate(id);
        if (cancelled) return;
        setTemplate(res.data);
        setMeta({
          title: res.data.title,
          description: res.data.description || '',
          category: res.data.category,
          audience: res.data.audience || ['all'],
          carrier_filter: res.data.carrier_filter || [],
          pricing_tiers: (res.data.pricing_tiers || []).map((t) => ({ quantity: t.quantity, price: t.price })),
          auto_notify: res.data.auto_notify !== false,
          visibility_scope: res.data.visibility_scope || 'all',
          visibility_agency_id: res.data.visibility_agency_id || null,
          // Phase 27.50 — Ecommerce Storefront
          is_purchasable: !!res.data.is_purchasable,
          unit_price: res.data.unit_price ?? '',
          cost: res.data.cost ?? '',
          sku: res.data.sku || '',
          stock_qty: res.data.stock_qty ?? '',
          weight_oz: res.data.weight_oz ?? '',
        });
        setFields((res.data.fields || []).map((f) => ({ field_id: f.field_id || newId(), key: f.key, label: f.label, type: f.type || 'text', required: !!f.required, helper_text: f.helper_text || '' })));
        setStep('edit');
        // Phase 27.24.1 — preview generation runs asynchronously in the
        // backend BackgroundTasks queue. Keep polling every 2 seconds
        // until it resolves so the sample preview appears the moment
        // it's ready (or the failure message renders).
        if (res.data.preview_status === 'processing') {
          pollTimer = setTimeout(fetchOnce, 2000);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load template');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    fetchOnce();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  if (!isAdmin) {
    return <div className="page-content"><p className="text-rose-500">Only KR admins can manage templates.</p></div>;
  }

  const handleUpload = async () => {
    if (!file || !meta.title) {
      toast.error('Title and sample file are required');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', meta.title);
      fd.append('description', meta.description);
      fd.append('category', meta.category);
      fd.append('audience', meta.audience.join(','));
      fd.append('carrier_filter', (meta.carrier_filter || []).join(','));
      fd.append('visibility_scope', meta.visibility_scope || 'all');
      if (meta.visibility_scope === 'agency_tree' && meta.visibility_agency_id) {
        fd.append('visibility_agency_id', meta.visibility_agency_id);
      }
      const res = await marketingAPI.uploadTemplate(fd);
      toast.success('Sample uploaded — now add the order form fields');
      navigate(`/marketing/${res.data.template_id}/edit`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setSaving(false); }
  };

  const addField = () => setFields([...fields, { field_id: newId(), key: '', label: '', type: 'text', required: false, helper_text: '' }]);
  const updateField = (idx, patch) => setFields(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeField = (idx) => setFields(fields.filter((_, i) => i !== idx));
  const moveField = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    const copy = [...fields];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setFields(copy);
  };

  const handleSave = async () => {
    if (!template) return;
    // Normalize keys & validate
    const cleaned = fields.map((f) => ({
      ...f,
      label: (f.label || '').trim(),
      key: f.key ? slugify(f.key) : slugify(f.label),
    })).filter((f) => f.label);
    if (cleaned.some((f) => !f.key)) {
      toast.error('Every field needs a label');
      return;
    }
    // Detect duplicate keys
    const keys = cleaned.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      toast.error('Field labels must be unique');
      return;
    }
    setSaving(true);
    try {
      const tiers = (meta.pricing_tiers || [])
        .map((t) => ({ quantity: parseInt(t.quantity, 10), price: parseFloat(t.price) }))
        .filter((t) => t.quantity > 0 && t.price > 0)
        .sort((a, b) => a.quantity - b.quantity);
      const parsedUnit = meta.is_purchasable && meta.unit_price !== '' ? parseFloat(meta.unit_price) : null;
      const payload = {
        ...meta,
        fields: cleaned,
        pricing_tiers: tiers,
        // Phase 27.50 — Ecommerce Storefront: parse numeric fields on
        // the client so blank inputs become null (backend treats null
        // as unlimited stock / no direct sale price / no weight).
        unit_price: parsedUnit,
        cost: meta.cost !== '' && meta.cost !== null ? parseFloat(meta.cost) : null,
        stock_qty: meta.stock_qty !== '' && meta.stock_qty !== null ? parseInt(meta.stock_qty, 10) : null,
        weight_oz: meta.weight_oz !== '' && meta.weight_oz !== null ? parseFloat(meta.weight_oz) : null,
        sku: (meta.sku || '').trim() || null,
      };
      // UX: "List this product in the storefront" should behave as a
      // one-click publish / unpublish. Toggling it must ALSO flip the
      // template's lifecycle status so the item actually appears (or
      // disappears) on the storefront without the admin having to hunt
      // for a separate Publish button. Archived products are left
      // untouched — those are an admin-managed permanent state.
      const hasValidPrice = (parsedUnit != null && parsedUnit > 0) || tiers.length > 0;
      const currentStatus = template?.status || 'draft';
      if (meta.is_purchasable && hasValidPrice) {
        payload.status = 'published';
      } else if (!meta.is_purchasable && currentStatus === 'published') {
        payload.status = 'draft';
      }
      const res = await marketingAPI.updateTemplate(template.template_id, payload);
      setTemplate(res.data);
      setFields((res.data.fields || []).map((f) => ({ ...f })));
      // Phase 27.50.C.32 — Confirmation toast that reflects the actual
      // storefront visibility change so admins get instant feedback.
      if (payload.status === 'published') {
        toast.success('Product published to shop');
      } else if (payload.status === 'draft' && currentStatus === 'published') {
        toast.success('Product removed from shop');
      } else {
        toast.success('Template saved');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleNotify = async () => {
    if (!template) return;
    if (!window.confirm('Send an email to all eligible agencies/agents about this material?')) return;
    setNotifying(true);
    try {
      const res = await marketingAPI.notifyTemplate(template.template_id);
      const { sent, recipients, failed, sent_preview } = res.data || {};
      if (sent > 0) toast.success(`Sent ${sent} email${sent === 1 ? '' : 's'}`);
      else if (recipients === 0) toast.info('No eligible recipients');
      else toast.error('No emails were sent');
      if (failed?.length) toast.error(`${failed.length} email${failed.length === 1 ? '' : 's'} failed`);
      // Phase 27.50.C.36 — Remember the recipient preview so the admin
      // can eyeball the first 25 addresses (helps confirm whether a
      // specific agent was actually included).
      setLastSendPreview({ sent, failed: failed || [], preview: sent_preview || [] });
      const reloaded = await marketingAPI.getTemplate(template.template_id);
      setTemplate(reloaded.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Notification failed');
    } finally { setNotifying(false); }
  };

  const runNotifyCheck = async () => {
    if (!template || !checkEmail.trim()) return;
    setChecking(true); setCheckResult(null);
    try {
      const r = await marketingAPI.checkNotifyRecipient(template.template_id, checkEmail.trim());
      setCheckResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check failed');
    } finally { setChecking(false); }
  };

  if (loading) return <div className="page-content flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;

  // ── Upload step ──
  if (step === 'upload') {
    return (
      <div data-testid="marketing-upload">
        <PageHeader
          title="Upload Marketing Sample"
          description="Upload an example image or PDF of the piece. Next you'll define the form fields agents will fill out."
          actions={<Link to="/marketing"><Button variant="outline"><ArrowLeft size={16} className="mr-2" />Back</Button></Link>}
        />
        <div className="page-content max-w-2xl">
          <Card>
            <CardHeader><CardTitle>Sample Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Sample file (PDF, PNG, JPG — max 25 MB)</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="template-file-input" />
                <p className="text-[11px] text-slate-500 mt-1">Multi-page PDFs (e.g. business cards with front + back) are supported.</p>
              </div>
              <div>
                <Label>Title *</Label>
                <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} placeholder="ACA Brochure 2026" data-testid="template-title-input" />
              </div>
              <div>
                <Label>Description</Label>
                <MarkdownEditor
                  value={meta.description}
                  onChange={(v) => setMeta({ ...meta, description: v })}
                  rows={3}
                  testId="upload-description-editor"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={meta.category} onValueChange={(v) => setMeta({ ...meta, category: v })}>
                    <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visible to</Label>
                  <VisibilityScopePicker
                    meta={meta}
                    setMeta={setMeta}
                    agencies={agencies}
                    testIdPrefix="upload"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => navigate('/marketing')}>Cancel</Button>
                <Button onClick={handleUpload} disabled={saving || !file || !meta.title} data-testid="upload-submit-btn">
                  {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Plus size={14} className="mr-2" />}
                  Upload &amp; Add Fields
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Edit step ──
  // The template loads asynchronously in the useEffect above. On the
  // first render `template` is still null, and the JSX below dereferences
  // `template.template_id` — which throws and trips the ErrorBoundary.
  // Render a loading state until the fetch completes.
  if (!isNew && !template) {
    return (
      <div className="page-content flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-slate-400" size={24} />
      </div>
    );
  }
  const pageCount = template?.page_count || 1;
  return (
    <div data-testid="marketing-editor">
      <PageHeader
        title={`Edit: ${meta.title}`}
        description="Define the form fields agents will fill out when ordering this piece."
        actions={
          <div className="flex gap-2">
            <Link to="/marketing"><Button variant="outline"><ArrowLeft size={16} className="mr-2" />Back</Button></Link>
            <Button variant="outline" onClick={handleNotify} disabled={notifying || saving || !template} data-testid="notify-template-btn">
              {notifying ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
              Notify Recipients
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save size={14} className="mr-2" />}
              Save
            </Button>
          </div>
        }
      />

      <div className="page-content grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          {/* Notify diagnostic panel (visible right after a send) */}
          {(lastSendPreview || template?.notified_at) && (
            <NotifyDiagnosticPanel
              lastSendPreview={lastSendPreview}
              notifiedAt={template?.notified_at}
              notifiedCount={template?.notified_count}
              checkEmail={checkEmail}
              setCheckEmail={setCheckEmail}
              checkResult={checkResult}
              checking={checking}
              onRunCheck={runNotifyCheck}
            />
          )}
          {/* Sample preview */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Sample {pageCount > 1 ? `(${pageCount} pages)` : ''}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {template.preview_status === 'processing' ? (
                <div className="h-48 bg-slate-50 border border-dashed rounded flex flex-col items-center justify-center gap-2 text-slate-500" data-testid="preview-processing">
                  <Loader2 className="animate-spin text-slate-400" size={22} />
                  <p className="text-sm">Rendering preview…</p>
                  <p className="text-xs text-slate-400">Large PDFs can take up to a minute.</p>
                </div>
              ) : template.preview_status === 'failed' ? (
                <div className="p-4 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700" data-testid="preview-failed">
                  <p className="font-medium">Preview generation failed</p>
                  <p className="text-xs mt-1">{template.preview_error || 'Unknown error'}</p>
                  <p className="text-xs mt-2 text-slate-600">You can still define fields and publish — agents will see the source file when ordering.</p>
                </div>
              ) : (
                Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                  <div key={n}>
                    {pageCount > 1 && <p className="text-xs text-slate-500 mb-1.5">{n === 1 ? 'Page 1 (Front)' : n === 2 ? 'Page 2 (Back)' : `Page ${n}`}</p>}
                    <PagePreview templateId={template.template_id} page={n} />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Cover image (storefront thumbnail) */}
          <CoverImageCard template={template} onChanged={(patch) => setTemplate((t) => ({ ...(t || {}), ...patch }))} />

          {/* Gallery — additional storefront angles */}
          <GalleryCard template={template} onChanged={(gallery) => setTemplate((t) => ({ ...(t || {}), gallery_images: gallery }))} />

          {/* Form fields */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Order Form Fields</CardTitle>
              <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn"><Plus size={14} className="mr-1.5" />Add field</Button>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No form fields yet. Add the info you need the agent to provide (Name, Phone, etc.).</p>
              ) : (
                <div className="space-y-3">
                  {fields.map((f, idx) => (
                    <div key={f.field_id} className="border rounded-lg p-3 bg-slate-50" data-testid={`field-row-${idx}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col gap-0.5 pt-1.5">
                          <button type="button" onClick={() => moveField(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><GripVertical size={14} /></button>
                          <span className="text-xs text-slate-400 text-center">{idx + 1}</span>
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-2">
                          <div>
                            <Label className="text-xs">Label *</Label>
                            <Input
                              value={f.label}
                              onChange={(e) => updateField(idx, { label: e.target.value })}
                              placeholder="e.g. Full Name"
                              data-testid={`field-label-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Type</Label>
                            <Select value={f.type} onValueChange={(v) => updateField(idx, { type: v })}>
                              <SelectTrigger data-testid={`field-type-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-end gap-2 pb-0.5">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox checked={f.required} onCheckedChange={(c) => updateField(idx, { required: !!c })} />
                              <span className="text-xs text-slate-700">Required</span>
                            </label>
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeField(idx)} data-testid={`remove-field-${idx}`}>
                              <Trash2 size={14} className="text-rose-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 pl-7">
                        <Input
                          value={f.helper_text || ''}
                          onChange={(e) => updateField(idx, { helper_text: e.target.value })}
                          placeholder="Helper text shown under this field (optional)"
                          className="text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm">Template Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <MarkdownEditor
                  value={meta.description}
                  onChange={(v) => setMeta({ ...meta, description: v })}
                  rows={4}
                  testId="edit-description-editor"
                />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={meta.category} onValueChange={(v) => setMeta({ ...meta, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Visible to</Label>
                <VisibilityScopePicker
                  meta={meta}
                  setMeta={setMeta}
                  agencies={agencies}
                  testIdPrefix="edit"
                  compact
                />
              </div>
              {template?.notified_at && (
                <div className="text-[11px] text-slate-500 border-t pt-2 mt-2">
                  Last notification: <span className="text-slate-700 font-medium">{new Date(template.notified_at).toLocaleString()}</span>
                  {typeof template.notified_count === 'number' && <> — sent to {template.notified_count}</>}
                </div>
              )}
              {!template?.notified_at && (
                <div className="flex items-start gap-2 border-t pt-3 mt-2" data-testid="auto-notify-block">
                  <Checkbox
                    id="auto-notify"
                    checked={meta.auto_notify}
                    onCheckedChange={(c) => setMeta({ ...meta, auto_notify: !!c })}
                    data-testid="auto-notify-checkbox"
                  />
                  <div className="min-w-0">
                    <Label htmlFor="auto-notify" className="cursor-pointer text-xs font-medium">
                      Email audience on first publish
                    </Label>
                    <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                      When you save fields for the first time, the audience above receives a &quot;new marketing material&quot; email. Uncheck to publish silently.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="pricing-tiers-card">
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm">Bulk Pricing Tiers</CardTitle>
                <CopyTiersButton
                  currentTemplateId={id}
                  onCopy={(tiers) => setMeta((m) => ({
                    ...m,
                    pricing_tiers: tiers.map((t) => ({ quantity: t.quantity, price: t.price })),
                  }))}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-[11px] text-slate-500">
                Optional. Each row = an exact quantity option and its total price on the storefront.
                Leave empty and the cart will fall back to <strong>unit price × quantity</strong>.
              </p>
              <div className="space-y-1.5">
                {(meta.pricing_tiers || []).map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      placeholder="Qty"
                      value={t.quantity}
                      onChange={(e) => {
                        const next = [...meta.pricing_tiers];
                        next[idx] = { ...next[idx], quantity: e.target.value };
                        setMeta({ ...meta, pricing_tiers: next });
                      }}
                      className="h-9 w-20 shrink-0 text-sm text-slate-900"
                      data-testid={`tier-qty-${idx}`}
                    />
                    <span className="text-slate-400 text-sm shrink-0">=</span>
                    <div className="relative flex-1 min-w-0">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">$</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="Total price"
                        value={t.price}
                        onChange={(e) => {
                          const next = [...meta.pricing_tiers];
                          next[idx] = { ...next[idx], price: e.target.value };
                          setMeta({ ...meta, pricing_tiers: next });
                        }}
                        className="h-9 w-full text-sm text-slate-900 pl-7"
                        data-testid={`tier-price-${idx}`}
                      />
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setMeta({ ...meta, pricing_tiers: meta.pricing_tiers.filter((_, i) => i !== idx) })}>
                      <Trash2 size={12} className="text-rose-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => setMeta({ ...meta, pricing_tiers: [...(meta.pricing_tiers || []), { quantity: '', price: '' }] })} data-testid="add-tier-btn">
                <Plus size={12} className="mr-1" />Add tier
              </Button>
            </CardContent>
          </Card>

          <Card data-testid="ecommerce-product-card">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                Ecommerce Product
                <span className="text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-300 rounded px-1.5 py-0.5">
                  Storefront
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-[11px] text-slate-500 -mt-1">
                Check this box to publish the template to the store. Agents can add it to a cart and check out with a card. Uncheck to hide from the shop again.
              </p>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-purchasable"
                  checked={meta.is_purchasable}
                  onCheckedChange={(c) => setMeta({ ...meta, is_purchasable: !!c })}
                  data-testid="is-purchasable-checkbox"
                />
                <Label htmlFor="is-purchasable" className="cursor-pointer text-xs">
                  List this product in the storefront (publish on save)
                </Label>
              </div>
              {meta.is_purchasable && (
                <div className="grid grid-cols-2 gap-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">Cost (my cost per unit)</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1.5 text-xs text-slate-400">$</span>
                      <Input
                        type="number" step="0.01" min="0"
                        placeholder="e.g. 9.99"
                        value={meta.cost}
                        onChange={(e) => setMeta({ ...meta, cost: e.target.value })}
                        className="pl-6"
                        data-testid="ecom-cost"
                      />
                    </div>
                    <SuggestedPriceHint cost={meta.cost} templateId={template?.template_id} />
                  </div>
                  <div>
                    <Label className="text-xs">Unit price (direct sale) *</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1.5 text-xs text-slate-400">$</span>
                      <Input
                        type="number" step="0.01" min="0"
                        placeholder="e.g. 24.99"
                        value={meta.unit_price}
                        onChange={(e) => setMeta({ ...meta, unit_price: e.target.value })}
                        className="pl-6"
                        data-testid="ecom-unit-price"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Stock quantity</Label>
                    <Input
                      type="number" min="0" step="1"
                      placeholder="Leave empty for unlimited"
                      value={meta.stock_qty}
                      onChange={(e) => setMeta({ ...meta, stock_qty: e.target.value })}
                      data-testid="ecom-stock-qty"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SKU</Label>
                    <Input
                      placeholder="e.g. KR-POST-001"
                      value={meta.sku}
                      onChange={(e) => setMeta({ ...meta, sku: e.target.value })}
                      data-testid="ecom-sku"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Weight (oz)</Label>
                    <Input
                      type="number" step="0.1" min="0"
                      placeholder="For shipping calc"
                      value={meta.weight_oz}
                      onChange={(e) => setMeta({ ...meta, weight_oz: e.target.value })}
                      data-testid="ecom-weight"
                    />
                  </div>
                  <p className="col-span-2 text-[11px] text-slate-500 pt-1">
                    Product images use the same preview pages of this template. Multiple pages =
                    multiple gallery images automatically. To change them, re-upload the template file.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}


// Phase 27.30.32 — Visibility scope + agency picker.
//
// Reused in both the upload flow and the edit sidebar. Keeps two related
// pieces of metadata in sync:
//   1. `audience`           — legacy tag list, kept for back-compat with
//                             the notify hook + email routing.
//   2. `visibility_scope`   — precise scoping rule the backend enforces
//                             on GET /marketing/templates.
// The mapping isn't 1:1 (audience is finer-grained than scope on the
// "agents only" side), so we drive both from a single dropdown value
// that expresses the KR admin's intent in plain English.
function VisibilityScopePicker({ meta, setMeta, agencies, testIdPrefix = 'scope', compact = false }) {
  // Derive the compound value from the current meta so the dropdown
  // reflects saved state after a reload.
  const currentValue = (() => {
    const scope = meta.visibility_scope || 'all';
    if (scope !== 'all') return scope;
    // scope === 'all' → the "audience" tag tells us if the KR admin
    // meant "agents only" or "everyone".
    const aud = meta.audience?.[0] || 'all';
    if (aud === 'agents') return 'agents_only';
    if (aud === 'agencies') return 'agencies_only_legacy';
    return 'all';
  })();

  const apply = (value) => {
    if (value === 'all') {
      setMeta({ ...meta, visibility_scope: 'all', visibility_agency_id: null, audience: ['all'] });
    } else if (value === 'agencies_only') {
      setMeta({ ...meta, visibility_scope: 'agencies_only', visibility_agency_id: null, audience: ['agencies'] });
    } else if (value === 'agents_only') {
      setMeta({ ...meta, visibility_scope: 'all', visibility_agency_id: null, audience: ['agents'] });
    } else if (value === 'kr_only') {
      setMeta({ ...meta, visibility_scope: 'kr_only', visibility_agency_id: null, audience: ['all'] });
    } else if (value === 'agency_tree') {
      setMeta({ ...meta, visibility_scope: 'agency_tree', audience: ['all'] });
    }
  };

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <Select value={currentValue === 'agencies_only_legacy' ? 'agencies_only' : currentValue} onValueChange={apply}>
        <SelectTrigger data-testid={`${testIdPrefix}-scope-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All agents + agencies</SelectItem>
          <SelectItem value="agencies_only">Only agencies</SelectItem>
          <SelectItem value="agents_only">Only KR agents</SelectItem>
          <SelectItem value="kr_only">Only KR Enterprise Group (HQ)</SelectItem>
          <SelectItem value="agency_tree">Specific agency + its downline</SelectItem>
        </SelectContent>
      </Select>
      {meta.visibility_scope === 'agency_tree' && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Choose agency</Label>
          <Select
            value={meta.visibility_agency_id || ''}
            onValueChange={(v) => setMeta({ ...meta, visibility_agency_id: v })}
          >
            <SelectTrigger data-testid={`${testIdPrefix}-agency-select`} className="mt-1">
              <SelectValue placeholder="Select an agency…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {(agencies || []).map((a) => (
                <SelectItem key={a.agency_id} value={a.agency_id}>
                  {a.agency_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-1">
            Everyone in this agency and every sub-agency below it will see this piece.
          </p>
        </div>
      )}
    </div>
  );
}


/**
 * CoverImageCard — Storefront thumbnail management (Phase 27.50.B.17).
 *
 * Admins can upload a dedicated image that replaces the raw PDF preview
 * on the shop grid. Falls back to the PDF preview automatically when
 * no cover is set.
 */
function CoverImageCard({ template, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const hasCover = !!template?.cover_image_path;

  useEffect(() => {
    let revoke;
    if (!template?.template_id || !hasCover) { setPreview(null); return undefined; }
    marketingAPI.coverImageBlob(template.template_id)
      .then((r) => {
        const u = URL.createObjectURL(r.data); revoke = u; setPreview(u);
      })
      .catch(() => setPreview(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [template?.template_id, template?.cover_image_uploaded_at, hasCover]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !template?.template_id) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Cover image must be under 10 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await marketingAPI.uploadCoverImage(template.template_id, fd);
      onChanged?.({
        cover_image_path: r.data?.cover_image_path || 'set',
        cover_image_uploaded_at: new Date().toISOString(),
      });
      toast.success('Cover image updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cover image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!template?.template_id) return;
    setRemoving(true);
    try {
      await marketingAPI.deleteCoverImage(template.template_id);
      onChanged?.({ cover_image_path: null, cover_image_uploaded_at: null });
      setPreview(null);
      toast.success('Cover image removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove cover image');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Card data-testid="cover-image-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <ImageIcon size={14} className="text-slate-500" />
          Storefront Cover Image
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Shown on the shop grid instead of the raw PDF preview. PNG,
          JPG, JPEG or WEBP, up to 10 MB.
        </p>
        <div className="flex items-center gap-4">
          <div className="w-32 h-24 rounded-md border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {preview ? (
              <img
                src={preview}
                alt="Cover"
                className="max-w-full max-h-full object-contain"
                data-testid="cover-image-preview"
              />
            ) : (
              <ImageIcon size={22} className="text-slate-300" />
            )}
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            <label
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer border ${
                uploading ? 'opacity-60 cursor-not-allowed' : ''
              } bg-white border-slate-300 hover:bg-slate-50 text-slate-700`}
              data-testid="cover-image-upload-label"
            >
              {uploading ? (
                <><Loader2 size={13} className="animate-spin" /> Uploading…</>
              ) : (
                <><Upload size={13} /> {hasCover ? 'Replace' : 'Upload'} cover</>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="cover-image-file-input"
              />
            </label>
            {hasCover && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRemove}
                disabled={removing}
                className="h-8 text-xs"
                data-testid="cover-image-remove-btn"
              >
                {removing ? (
                  <><Loader2 size={13} className="mr-1.5 animate-spin" /> Removing…</>
                ) : (
                  <><X size={13} className="mr-1.5" /> Remove</>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * GalleryCard — Multiple product angles (Phase 27.50.B.18).
 *
 * Admins upload up to 8 secondary images shown alongside the cover in
 * the storefront product dialog. Order is preserved on the server;
 * reorder via native HTML5 drag-and-drop or the up/down arrows.
 */
function GalleryCard({ template, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const gallery = template?.gallery_images || [];
  const canAddMore = gallery.length < 8 && template?.template_id;

  const commitReorder = async (next) => {
    if (!template?.template_id) return;
    const prev = gallery;
    onChanged?.(next);
    try {
      await marketingAPI.reorderGallery(template.template_id, next.map((g) => g.image_id));
    } catch (err) {
      onChanged?.(prev);
      toast.error(err?.response?.data?.detail || 'Reorder failed');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !template?.template_id) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Each image must be under 10 MB');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await marketingAPI.uploadGalleryImage(template.template_id, fd);
      onChanged?.(r.data?.gallery_images || []);
      toast.success('Image added to gallery');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const handleDelete = async (imageId) => {
    if (!template?.template_id) return;
    setBusy(true);
    try {
      const r = await marketingAPI.deleteGalleryImage(template.template_id, imageId);
      onChanged?.(r.data?.gallery_images || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const move = (from, to) => {
    if (to < 0 || to >= gallery.length || from === to) return;
    const next = [...gallery];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    commitReorder(next);
  };

  const onDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== idx) setOverIndex(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== idx) move(dragIndex, idx);
    setDragIndex(null); setOverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  return (
    <Card data-testid="gallery-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <ImageIcon size={14} className="text-slate-500" />
          Product Gallery
          <span className="text-[10px] text-slate-500 font-normal">({gallery.length}/8)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Additional angles shown on the storefront product dialog. Drag to
          reorder or use the arrows. PNG, JPG, JPEG or WEBP, up to 10 MB each.
        </p>
        {gallery.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {gallery.map((img, idx) => (
              <GalleryThumb
                key={img.image_id}
                templateId={template.template_id}
                image={img}
                index={idx}
                total={gallery.length}
                isDropTarget={overIndex === idx}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
                onMoveUp={() => move(idx, idx - 1)}
                onMoveDown={() => move(idx, idx + 1)}
                onDelete={() => handleDelete(img.image_id)}
                disabled={busy}
              />
            ))}
          </div>
        )}
        {canAddMore && (
          <label
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer border ${
              busy ? 'opacity-60 cursor-not-allowed' : ''
            } bg-white border-slate-300 hover:bg-slate-50 text-slate-700`}
            data-testid="gallery-upload-label"
          >
            {busy ? (
              <><Loader2 size={13} className="animate-spin" /> Working…</>
            ) : (
              <><Upload size={13} /> Add image</>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={handleUpload}
              disabled={busy}
              data-testid="gallery-file-input"
            />
          </label>
        )}
        {!canAddMore && template?.template_id && (
          <p className="text-[11px] text-slate-500">Gallery is full — remove one to add another.</p>
        )}
      </CardContent>
    </Card>
  );
}

function GalleryThumb({
  templateId, image, index, total, isDropTarget, disabled,
  onDragStart, onDragOver, onDrop, onDragEnd,
  onMoveUp, onMoveDown, onDelete,
}) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    marketingAPI.galleryImageBlob(templateId, image.image_id)
      .then((r) => { const u = URL.createObjectURL(r.data); revoke = u; setSrc(u); })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, image.image_id]);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative rounded-md border bg-white overflow-hidden group ${
        isDropTarget ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-slate-200'
      }`}
      data-testid={`gallery-thumb-${index}`}
    >
      <div className="w-full h-24 flex items-center justify-center bg-slate-50">
        {src ? (
          <img src={src} alt="" className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon size={20} className="text-slate-300" />
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-white/95 border-t border-slate-100 flex items-center justify-between px-1.5 py-1">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={disabled || index === 0}
            className="text-slate-500 hover:text-slate-800 disabled:opacity-30 p-0.5"
            title="Move earlier"
            data-testid={`gallery-move-up-${index}`}
          >
            <ArrowLeft size={12} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={disabled || index === total - 1}
            className="text-slate-500 hover:text-slate-800 disabled:opacity-30 p-0.5"
            title="Move later"
            data-testid={`gallery-move-down-${index}`}
          >
            <ArrowLeft size={12} className="rotate-180" />
          </button>
        </div>
        <span className="text-[10px] text-slate-500">#{index + 1}</span>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          className="text-rose-500 hover:text-rose-700 disabled:opacity-40 p-0.5"
          title="Remove image"
          data-testid={`gallery-delete-${index}`}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

/**
 * CopyTiersButton — Bulk Tier Copy (Phase 27.50.B.23).
 *
 * Opens a picker of every other template that already has at least
 * one pricing tier so the admin can copy them into the current
 * product with one click. Lets you set up a family of pieces
 * (business cards, brochures, postcards…) in seconds instead of
 * retyping every row.
 */
function CopyTiersButton({ currentTemplateId, onCopy }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    marketingAPI.listTemplates()
      .then((r) => {
        const all = (r.data?.templates || []).filter(
          (t) => t.template_id !== currentTemplateId
            && Array.isArray(t.pricing_tiers) && t.pricing_tiers.length > 0,
        );
        setItems(all);
      })
      .catch(() => toast.error('Could not load templates'))
      .finally(() => setLoading(false));
  }, [open, currentTemplateId]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? items.filter((t) => (t.title || '').toLowerCase().includes(q))
    : items;

  const applyCopy = (tpl) => {
    const tiers = (tpl.pricing_tiers || [])
      .filter((t) => (t?.quantity || 0) > 0 && (t?.price || 0) > 0);
    if (tiers.length === 0) {
      toast.error('That template has no usable tiers');
      return;
    }
    onCopy(tiers);
    setOpen(false);
    toast.success(`Copied ${tiers.length} tier${tiers.length === 1 ? '' : 's'} from "${tpl.title}"`);
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        onClick={() => setOpen(true)}
        data-testid="copy-tiers-btn"
      >
        <Copy size={12} className="mr-1.5" />
        Copy from another product
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="copy-tiers-dialog">
          <DialogHeader>
            <DialogTitle>Copy pricing tiers</DialogTitle>
            <DialogDescription>
              Pick a product to import its tier list. Any tiers currently
              on this product will be replaced.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title…"
              className="pl-9 h-9 text-sm"
              data-testid="copy-tiers-search"
            />
          </div>
          <div className="max-h-80 overflow-y-auto -mx-1 pr-1 space-y-1.5">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 py-6 justify-center text-sm">
                <Loader2 size={14} className="animate-spin" /> Loading templates…
              </div>
            ) : visible.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-6" data-testid="copy-tiers-empty">
                {q
                  ? `No products matching "${query}"`
                  : 'No other products have pricing tiers yet.'}
              </p>
            ) : (
              visible.map((tpl) => {
                const preview = (tpl.pricing_tiers || [])
                  .slice(0, 3)
                  .map((t) => `${t.quantity} @ $${Number(t.price).toFixed(2)}`)
                  .join(' · ');
                const extra = (tpl.pricing_tiers?.length || 0) - 3;
                return (
                  <button
                    key={tpl.template_id}
                    type="button"
                    onClick={() => applyCopy(tpl)}
                    data-testid={`copy-tiers-source-${tpl.template_id}`}
                    className="w-full text-left rounded-md border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition p-3 group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{tpl.title}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {preview}{extra > 0 ? ` · +${extra} more` : ''}
                        </p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 group-hover:text-emerald-700 shrink-0 mt-1">
                        {tpl.pricing_tiers?.length || 0} tier{(tpl.pricing_tiers?.length || 0) === 1 ? '' : 's'}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * SuggestedPriceHint — Under the Cost input, offers an AI-generated
 * retail price suggestion with a healthy 15-30% margin. Fires when
 * the admin taps the "Suggest" chip after typing a positive cost.
 * Display-only: the admin still decides what to put in Unit price.
 */
function SuggestedPriceHint({ cost, templateId }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [err, setErr] = useState(null);
  const parsed = parseFloat(cost);
  const canSuggest = Number.isFinite(parsed) && parsed > 0 && !!templateId;
  const request = async () => {
    setLoading(true); setErr(null); setSuggestion(null);
    try {
      const r = await marketingAPI.suggestPrice(templateId, parsed);
      setSuggestion(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not fetch suggestion');
    } finally { setLoading(false); }
  };
  return (
    <div className="mt-1.5 text-[11px] text-slate-600" data-testid="suggested-price-hint">
      {!suggestion && !loading && (
        <button
          type="button"
          disabled={!canSuggest}
          onClick={request}
          data-testid="suggest-price-btn"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border transition ${
            canSuggest
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
              : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          }`}
        >
          AI suggest a healthy sale price
        </button>
      )}
      {loading && (
        <span className="inline-flex items-center gap-1 text-slate-500">
          <Loader2 size={12} className="animate-spin" /> Analyzing product…
        </span>
      )}
      {err && <span className="text-rose-600">{err}</span>}
      {suggestion && (
        <div className="mt-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5" data-testid="suggested-price-result">
          <div className="font-semibold text-emerald-900">
            Suggested: ${Number(suggestion.suggested_price).toFixed(2)}
            <span className="ml-1 text-emerald-700 font-normal">
              ({suggestion.margin_pct}% margin)
            </span>
          </div>
          {suggestion.rationale && (
            <p className="text-[10.5px] text-emerald-800/80 italic mt-0.5">{suggestion.rationale}</p>
          )}
          <button
            type="button"
            onClick={request}
            className="text-[10px] underline text-emerald-700 mt-1"
            data-testid="suggest-price-retry"
          >
            Regenerate
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * NotifyDiagnosticPanel — Shown after a "Notify Recipients" send.
 * Lets the admin (a) eyeball the first 25 recipients (to confirm a
 * specific agent was included) and (b) look up any email address to
 * see whether that user is eligible, and if not, WHY (inactive
 * status, missing role, disabled prefs, etc.).
 */
function NotifyDiagnosticPanel({
  lastSendPreview,
  notifiedAt,
  notifiedCount,
  checkEmail,
  setCheckEmail,
  checkResult,
  checking,
  onRunCheck,
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-xs space-y-3" data-testid="notify-diagnostic-panel">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-800">Notification diagnostics</div>
        {notifiedAt && (
          <span className="text-[10.5px] text-slate-500">
            Last sent: {new Date(notifiedAt).toLocaleString()}
            {typeof notifiedCount === 'number' && <span className="ml-1">· {notifiedCount} email(s)</span>}
          </span>
        )}
      </div>
      {lastSendPreview?.preview?.length > 0 && (
        <details className="rounded bg-white border border-slate-200 px-2 py-1">
          <summary className="cursor-pointer text-slate-700 text-[11.5px] font-medium">
            First {lastSendPreview.preview.length} recipients of the latest send
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono text-[10.5px] text-slate-600">
            {lastSendPreview.preview.map((e) => (<div key={e}>{e}</div>))}
          </div>
        </details>
      )}
      {lastSendPreview?.failed?.length > 0 && (
        <div className="text-[10.5px] text-rose-600">
          Failed: {lastSendPreview.failed.join(', ')}
        </div>
      )}
      <div className="space-y-1.5">
        <div className="text-[11px] text-slate-600">
          Not sure if a specific agent got the email? Type their address to check:
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="agent@example.com"
            value={checkEmail}
            onChange={(e) => setCheckEmail(e.target.value)}
            className="flex-1 h-8 rounded border border-slate-300 px-2 text-xs"
            data-testid="notify-check-email"
          />
          <button
            type="button"
            onClick={onRunCheck}
            disabled={!checkEmail.trim() || checking}
            className="h-8 rounded bg-slate-900 text-white px-3 text-xs disabled:opacity-50"
            data-testid="notify-check-btn"
          >
            {checking ? 'Checking…' : 'Check'}
          </button>
        </div>
        {checkResult && (
          <div
            className={`mt-1.5 rounded border px-2 py-1.5 text-[11px] ${
              checkResult.eligible
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
            data-testid="notify-check-result"
          >
            <div className="font-semibold">
              {checkResult.eligible ? 'This user IS eligible' : 'This user is NOT eligible'}
            </div>
            {checkResult.reasons?.length > 0 && (
              <ul className="mt-0.5 list-disc list-inside">
                {checkResult.reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            {checkResult.user_id && (
              <div className="mt-1 text-[10px] text-slate-500 space-y-0.5">
                <div>Name: {checkResult.name || '—'}</div>
                <div>Roles: {(checkResult.roles || []).join(', ') || 'none'}</div>
                <div>Status: {checkResult.status || '—'}</div>
                {checkResult.last_send_included_you === true && (
                  <div className="text-emerald-700">
                    Included in the last send. If they still did not get it, ask them to search their inbox for the sender email or check Spam.
                  </div>
                )}
                {checkResult.last_send_included_you === false && (
                  <div className="text-rose-700">Was NOT part of the last send.</div>
                )}
                {checkResult.last_send_failed_you && (
                  <div className="text-rose-700">Send FAILED for this address in the last batch.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

